import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { paymentApi } from '../../services/api';
import membershipApi from '../../services/membershipApi';

const CheckoutForm = ({ selectedPackage, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    let isMounted = true;
    if (selectedPackage) {
      (async () => {
        try {
          const response = await paymentApi.createPaymentIntent(selectedPackage.id);
          if (isMounted) setClientSecret(response.data.client_secret);
        } catch (error) {
          if (isMounted) toast.error('Failed to initialize payment');
          console.error('Payment intent error:', error);
        }
      })();
    }
    return () => { isMounted = false; };
  }, [selectedPackage]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setIsProcessing(true);

    const cardElement = elements.getElement(CardElement);

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (error) {
      toast.error(`Payment failed: ${error.message}`);
    } else if (paymentIntent.status === 'succeeded') {
      toast.success('Payment successful! Tokens will be credited shortly.');
      onSuccess();
    }

    setIsProcessing(false);
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '14px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Complete Purchase</h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>
      
      <div className="bg-gray-50 p-3 rounded mb-4">
        <div className="flex justify-between items-center text-sm">
          <span>{selectedPackage?.description}</span>
          <span className="font-bold">${(selectedPackage?.price / 100).toFixed(2)}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Information
          </label>
          <div className="border rounded-md p-3 bg-white">
            <CardElement options={cardElementOptions} />
          </div>
        </div>

        <button
          type="submit"
          disabled={!stripe || isProcessing || !clientSecret}
          className="btn-primary btn-md w-full"
        >
          {isProcessing ? 'Processing...' : `Pay $${(selectedPackage?.price / 100).toFixed(2)}`}
        </button>
      </form>
    </div>
  );
};

const TokenPurchaseSection = () => {
  const [packages, setPackages] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [userBalance, setUserBalance] = useState(0);
  const [stripePromise, setStripePromise] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    loadTokenPackages();
    loadUserBalance();
    initializeStripe();
  }, []);

  const initializeStripe = async () => {
    try {
      const response = await paymentApi.createPaymentIntent('20');
      const stripe = await loadStripe(response.data.publishable_key);
      setStripePromise(stripe);
    } catch (error) {
      console.error('Stripe initialization error:', error);
    }
  };

  const loadTokenPackages = async () => {
    try {
      const response = await paymentApi.getTokenPackages();
      setPackages(response.data.packages);
    } catch (error) {
      toast.error('Failed to load packages');
    }
  };

  const loadUserBalance = async () => {
    try {
      const response = await membershipApi.getCreditBalance();
      setUserBalance(response.data.credits);
    } catch (error) {
      console.error('Failed to load balance:', error);
    }
  };

  const handlePackageSelect = (packageId) => {
    const packageData = packages[packageId];
    setSelectedPackage({
      id: packageId,
      ...packageData
    });
    setShowCheckout(true);
  };

  const handlePaymentSuccess = () => {
    setShowCheckout(false);
    setSelectedPackage(null);
    loadUserBalance();
  };

  const handleCancel = () => {
    setShowCheckout(false);
    setSelectedPackage(null);
  };

  const getSavingsPercentage = (packageId) => {
    const basePrice = 199;
    const package_data = packages[packageId];
    if (!package_data || packageId === '20') return 0;
    
    const expectedPrice = (package_data.tokens / 20) * basePrice;
    const savings = ((expectedPrice - package_data.price) / expectedPrice) * 100;
    return Math.round(savings);
  };

  if (showCheckout && stripePromise) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 mb-16">
        <div className="max-w-md mx-auto">
          <Elements stripe={stripePromise}>
            <CheckoutForm 
              selectedPackage={selectedPackage}
              onSuccess={handlePaymentSuccess}
              onCancel={handleCancel}
            />
          </Elements>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-8 mb-16">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Need More Credits?</h2>
        <p className="text-gray-600">
          Current Balance: <span className="font-semibold text-primary-600">{userBalance} tokens</span>
        </p>
        <p className="text-gray-500 text-sm mt-1">Purchase additional tokens anytime</p>
      </div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-6xl mx-auto">
        {Object.entries(packages).map(([packageId, packageData]) => {
          const savings = getSavingsPercentage(packageId);
          return (
            <div 
              key={packageId}
              className="bg-white rounded-lg p-4 border border-gray-200 hover:border-primary-300 transition-colors cursor-pointer relative"
              onClick={() => handlePackageSelect(packageId)}
            >
              {savings > 0 && (
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  Save {savings}%
                </div>
              )}
              
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 mb-1">
                  {packageData.tokens} Tokens
                </div>
                <div className="text-xl font-bold text-primary-600 mb-2">
                  ${(packageData.price / 100).toFixed(2)}
                </div>
                <p className="text-gray-500 text-xs mb-3">
                  ${(packageData.price / packageData.tokens / 100).toFixed(3)} per token
                </p>
                
                <button className="btn-primary btn-sm w-full">
                  Select
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="text-center mt-4">
        <p className="text-xs text-gray-500">
          Secure payments powered by Stripe • Tokens credited instantly
        </p>
      </div>
    </div>
  );
};

export default TokenPurchaseSection;